/**
 * Refine-wording undo snapshots (MDRS-165).
 *
 * When the editor's "Refine Wording" replaces a selection, we keep a snapshot so
 * "Original Wording" can revert it. The original bug: the snapshot was keyed by
 * the *exact* refined string and looked up by the *live selection's* extracted
 * text, so any whitespace/normalisation difference — or a lagging/empty
 * selection on the first click — meant the lookup missed and revert failed.
 *
 * This tracks refinements as a list scoped by paragraph and matches on
 * *normalised* text (and, when the live selection lags, by locating the refined
 * text inside the paragraph), so revert is robust to those differences.
 */
export type Refinement = {
  paraId: string | null;
  original: string;
  refined: string;
  /** Serialised ProseMirror slice (`Slice.toJSON()`) of the exact original
   *  content that was replaced, so revert can restore paragraph structure
   *  verbatim rather than re-inserting a flat string as inline text (which
   *  merged paragraphs on a multi-paragraph selection). Optional: kept `unknown`
   *  so this pure module stays free of a prosemirror import; EditorOverlays does
   *  the (de)serialisation. Absent snapshots fall back to the string path. */
  originalSliceJSON?: unknown;
};

/** Collapse runs of whitespace and trim — the matching normal form. */
export const normalizeForMatch = (s: string): string =>
  (s ?? "").replace(/\s+/g, " ").trim();

/** Strip ALL whitespace — a separator-insensitive normal form for comparing
 *  the editor's `getSelectionInfo().selectedText` (which concatenates adjacent
 *  paragraphs with NO delimiter, e.g. "…contexts.I am trained…") against text
 *  that carries paragraph breaks (a `textBetween` extract, or the refine
 *  service's output). A collapse-to-single-space normal form leaves those two
 *  differing by exactly the boundary space, which defeated matching on any
 *  multi-paragraph selection. */
export const stripWs = (s: string): string => (s ?? "").replace(/\s+/g, "");

/**
 * Add a refinement. Re-refining the same text in the same paragraph replaces the
 * existing snapshot (matched on normalised refined text) rather than appending.
 */
export function recordRefinement(
  list: Refinement[],
  refinement: Refinement
): Refinement[] {
  const key = normalizeForMatch(refinement.refined);
  const kept = list.filter(
    (r) => !(r.paraId === refinement.paraId && normalizeForMatch(r.refined) === key)
  );
  return [...kept, refinement];
}

/**
 * Find the refinement to revert for the current selection/paragraph, or null.
 *
 * Preference order:
 *  1. refinements in the current paragraph (fall back to all if none there);
 *  2. exact normalised match of the selection to a refined snapshot;
 *  3. selection contains / is contained by a refined snapshot (partial select);
 *  4. selection lags empty → the paragraph text still contains a refined snapshot.
 */
export function findRefinementToRevert(
  list: Refinement[],
  ctx: { paraId: string | null; selectedText: string; paragraphText: string }
): Refinement | null {
  if (!list.length) return null;

  const inPara = list.filter((r) => r.paraId === ctx.paraId);
  const pool = inPara.length ? inPara : list;

  // Match with ALL whitespace stripped: the live selectedText joins adjacent
  // paragraphs with no separator, while a stored `refined` carries the boundary
  // as a space/newline — a collapse-to-space normal form would never match a
  // multi-paragraph refinement.
  const sel = stripWs(ctx.selectedText);
  if (sel) {
    // A non-empty selection is an explicit target: match it against a refined
    // snapshot (exact, or a substantial partial overlap) and otherwise return
    // null — do NOT fall back to the paragraph body, or a stray selection that
    // happens to sit in a refined paragraph would revert unrelated text.
    const exact = pool.find((r) => stripWs(r.refined) === sel);
    if (exact) return exact;

    // Partial: the selection spans a refined snapshot, or is a substantial
    // sub-selection of one. Require a meaningful length for the "selection
    // inside refined" direction so a short stray selection (a single common
    // word) can't match an unrelated refinement.
    const partial = pool.find((r) => {
      const ref = stripWs(r.refined);
      if (ref.length === 0) return false;
      return sel.includes(ref) || (sel.length >= 12 && ref.includes(sel));
    });
    return partial ?? null;
  }

  // Live selection lagged empty (first-click case): revert the refinement whose
  // refined text is still present in the paragraph.
  const para = stripWs(ctx.paragraphText);
  const byParagraph = pool.find((r) => {
    const ref = stripWs(r.refined);
    return ref.length > 0 && para.includes(ref);
  });
  return byParagraph ?? null;
}

/**
 * Locate the single occurrence of ``refined`` in ``paragraphText`` for range
 * resolution. Returns its index only when it appears **exactly once** — when it
 * is absent (0) or ambiguous (>1) it returns ``-1`` so the caller bails with an
 * error rather than reverting an occurrence the user never refined (MDRS-165).
 */
export function locateUniqueOccurrence(
  paragraphText: string,
  refined: string
): number {
  if (!refined) return -1;
  const first = paragraphText.indexOf(refined);
  if (first < 0) return -1;
  const second = paragraphText.indexOf(refined, first + refined.length);
  return second < 0 ? first : -1;
}
